import React, { useState } from 'react';
import { cn } from '@/lib/utils';
import { MarketingTarotCard } from './MarketingTarotCard';
import { Button } from '@/components/ui/button';
import { Trash2, History, Clock, ChevronLeft, ChevronRight } from 'lucide-react';

const PAGE_SIZE = 3;

export const CardHistory = ({ 
  history = [], 
  onClearHistory,
  onSelectCard,
  className 
}) => {
  const [page, setPage] = useState(0);
  const totalPages = Math.max(1, Math.ceil(history.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages - 1);
  const visible = history.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE);

  if (history.length === 0) {
    return (
      <div className={cn(
        "bg-[#1e2a4a]/60 backdrop-blur-sm border-2 border-[#d4c4a8]/20 rounded-xl p-6",
        className
      )}>
        <div className="flex items-center gap-2 mb-4">
          <History className="w-5 h-5 text-[#e89b47]"/>
          <h3 className="font-display text-lg text-[#e8dcc8]">Card History</h3>
        </div>
        <div className="text-center py-12">
          <div className="text-[#d4c4a8]/30 text-4xl mb-3">✦</div>
          <p className="text-[#d4c4a8]/50 font-body text-sm">
            No cards drawn yet
          </p>
          <p className="text-[#d4c4a8]/30 font-body text-xs mt-1">
            Your drawn cards will appear here
          </p>
        </div>
        <div className="border-t border-[#d4c4a8]/10 pt-4 mt-4">
          <p className="text-[#d4c4a8]/40 font-body text-xs text-center">
            Click on a card from the deck above to start
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={cn(
      "bg-[#1e2a4a]/60 backdrop-blur-sm border-2 border-[#d4c4a8]/20 rounded-xl p-6",
      className
    )}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <History className="w-5 h-5 text-[#e89b47]"/>
          <h3 className="font-display text-lg text-[#e8dcc8]">Card History</h3>
          <span className="text-[#d4c4a8]/50 text-sm">({history.length})</span>
        </div>
        <Button
          onClick={onClearHistory}
          variant="ghost"
          size="sm"
          className="text-[#d4c4a8]/50 hover:text-[#d4c4a8] hover:bg-[#d4c4a8]/10"
        >
          <Trash2 className="w-4 h-4 mr-1"/>
          Clear
        </Button>
      </div>

      {/* History List - 3 cards per page */}
      <div className="space-y-4" data-testid="card-history-list">
        {visible.map((item, i) => {
          const index = safePage * PAGE_SIZE + i;
          return (
            <div
              key={`${item.card.id}-${item.timestamp}`}
              className={cn(
                "group flex items-center gap-4 p-3 rounded-lg",
                "bg-[#1a3d4a]/50 border border-[#d4c4a8]/10",
                "hover:border-[#d4c4a8]/30 hover:bg-[#1a3d4a]/70",
                "transition-all duration-300 cursor-pointer"
              )}
              onClick={() => onSelectCard?.(item.card)}
            >
              {/* Mini Card Preview */}
              <div className="flex-shrink-0 transform group-hover:scale-105 transition-transform">
                <MarketingTarotCard
                  card={item.card}
                  isFlipped={true}
                  size="small"
                  isHoverable={false}
                />
              </div>

              {/* Card Info */}
              <div className="flex-1 min-w-0 overflow-hidden">
                <h4 className="font-display text-[#e8dcc8] text-sm leading-tight line-clamp-2">
                  {item.card.name}
                </h4>
                {item.card.isReversed && (
                  <span className="text-[#e89b47]/70 text-xs">Reversed</span>
                )}
                <div className="flex flex-wrap gap-1 mt-1">
                  {item.card.keywords?.slice(0, 2).map((keyword, idx) => (
                    <span 
                      key={idx}
                      className="text-[#d4c4a8]/40 text-xs"
                    >
                      {keyword}{idx < 1 && ' •'}
                    </span>
                  ))}
                </div>
                <div className="flex items-center gap-1 mt-2 text-[#d4c4a8]/30 text-xs">
                  <Clock className="w-3 h-3"/>
                  {new Date(item.timestamp).toLocaleTimeString([], { 
                    hour: '2-digit', 
                    minute: '2-digit' 
                  })}
                </div>
              </div>

              {/* Index */}
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#d4c4a8]/10 border border-[#d4c4a8]/20 flex items-center justify-center">
                <span className="font-display text-[#d4c4a8]/60 text-xs">
                  #{history.length - index}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-[#d4c4a8]/10" data-testid="card-history-pagination">
          <Button
            onClick={() => setPage(Math.max(0, safePage - 1))}
            disabled={safePage === 0}
            variant="ghost"
            size="sm"
            className="text-[#d4c4a8]/60 hover:text-[#e89b47] hover:bg-[#d4c4a8]/10 disabled:opacity-30"
            data-testid="history-prev-btn"
          >
            <ChevronLeft className="w-4 h-4 mr-1"/>
            Newer
          </Button>
          <span className="font-display text-xs text-[#d4c4a8]/50 tracking-widest" data-testid="history-page-indicator">
            {safePage + 1} / {totalPages}
          </span>
          <Button
            onClick={() => setPage(Math.min(totalPages - 1, safePage + 1))}
            disabled={safePage >= totalPages - 1}
            variant="ghost"
            size="sm"
            className="text-[#d4c4a8]/60 hover:text-[#e89b47] hover:bg-[#d4c4a8]/10 disabled:opacity-30"
            data-testid="history-next-btn"
          >
            Older
            <ChevronRight className="w-4 h-4 ml-1"/>
          </Button>
        </div>
      )}
    </div>
  );
};

export default CardHistory;
